import type { AiModelsCatalogClient } from "../catalog/AiModelsCatalogClient.js";
import { UnknownModelCostError } from "../errors.js";
import { normalizeString } from "../sync/modelNameResolver/normalize.js";
import { stripModelVersionSuffix } from "../sync/modelNameResolver/stripVersionSuffix.js";
import type {
  ModelResolutionResult,
  ModelResolverOptions,
} from "../sync/modelNameResolver/types.js";
import { matchModelInAiProfiles } from "./aiProfilesMatch.js";
import {
  buildCatalogResolveAttempts,
  isLocalProvider,
  isLocalProviderResolution,
  resolveFromCatalogAttempts,
  resolveRoutedViaOpenRouter,
} from "./costModelResolution.js";
import {
  aiProfilesPricingToCatalogPricing,
  computeCostFromPricing,
} from "./profilePricing.js";
import { enrichCostResult, toCostExtraction } from "./enrichCostResult.js";
import {
  extractUsageInput,
  type ExtractUsageInputOptions,
} from "./extractUsageInput.js";
import { resolveUsageModel } from "./resolveUsageModel.js";
import type { AiCostResult, AiCostWarning, AiModelPricing, AiUsageInput } from "./types.js";

export type CostCalculatorOptions = {
  includeBreakdown?: boolean;
  resolverOptions?: ModelResolverOptions;
  /** When true, throw {@link UnknownModelCostError} instead of zero-cost fallback. */
  throwOnUnknownModel?: boolean;
};

function emptyPricing(): AiModelPricing {
  return {
    promptUsdPerToken: 0,
    completionUsdPerToken: 0,
    imageUsdPerUnit: 0,
    requestUsdPerRequest: 0,
    pricedAt: new Date().toISOString(),
    source: "manual",
  };
}

function mergeAttempts(
  ...groups: ReturnType<typeof buildCatalogResolveAttempts>[]
): ReturnType<typeof buildCatalogResolveAttempts> {
  const seen = new Set<string>();
  const out: ReturnType<typeof buildCatalogResolveAttempts> = [];
  for (const group of groups) {
    for (const a of group) {
      const key = `${a.provider ?? ""}\0${a.model}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(a);
    }
  }
  return out;
}

export class CostCalculator {
  private readonly includeBreakdown: boolean;
  private readonly resolverOptions?: ModelResolverOptions;
  private readonly throwOnUnknownModel: boolean;

  constructor(
    private readonly catalog: AiModelsCatalogClient,
    options: CostCalculatorOptions = {},
  ) {
    this.includeBreakdown = options.includeBreakdown ?? true;
    this.resolverOptions = options.resolverOptions;
    this.throwOnUnknownModel = options.throwOnUnknownModel ?? false;
  }

  /**
   * Extract model, provider, and token usage from an activity / gateway record
   * (MongoDB export, nested `outer`, `config`, `metadata.diagnostics`, etc.)
   * then run {@link calculate}.
   */
  async calculateFromRecord(
    record: unknown,
    extractOptions?: ExtractUsageInputOptions,
  ): Promise<AiCostResult> {
    const { input, provenance } = extractUsageInput(record, extractOptions);
    const warnings: AiCostWarning[] = [];
    if (provenance.tokens.estimated) {
      warnings.push({
        code: "ESTIMATED_TOKEN_USAGE",
        message: `Token counts are estimates from ${provenance.tokens.path} (not billed usage).`,
      });
    }
    const result = await this.calculate(input);
    const enriched = enrichCostResult(result, input, toCostExtraction(provenance));
    if (warnings.length > 0) {
      enriched.warnings = [...(enriched.warnings ?? []), ...warnings];
    }
    return enriched;
  }

  async calculate(input: AiUsageInput): Promise<AiCostResult> {
    const modelInput = resolveUsageModel(input);
    const resolverOpts = this.resolverOptions;

    let resolved = await resolveFromCatalogAttempts(
      this.catalog,
      buildCatalogResolveAttempts(modelInput, input.provider),
      resolverOpts,
    );

    if (isLocalProviderResolution(resolved, input.provider)) {
      return this.localProviderResult(modelInput, input, resolved);
    }

    let profileMatch = null as Awaited<ReturnType<typeof matchModelInAiProfiles>>;
    const warnings: AiCostWarning[] = [];

    if (!resolved?.found || !resolved.record) {
      if (!isLocalProvider(input.provider)) {
        profileMatch = await matchModelInAiProfiles(modelInput, input.provider, {
          catalogLane: this.resolverOptions?.catalogLane,
        });
      }
      if (profileMatch) {
        resolved = await resolveFromCatalogAttempts(
          this.catalog,
          mergeAttempts(
            buildCatalogResolveAttempts(
              profileMatch.canonicalModelId,
              input.provider ?? profileMatch.provider,
            ),
            buildCatalogResolveAttempts(
              profileMatch.canonicalModelId,
              profileMatch.provider,
            ),
          ),
          resolverOpts,
        );
      }

      if (isLocalProviderResolution(resolved, input.provider)) {
        return this.localProviderResult(modelInput, input, resolved);
      }

      if ((!resolved?.found || !resolved.record) && profileMatch?.pricing) {
        const routedViaOpenRouter = resolveRoutedViaOpenRouter(
          input.provider,
          resolved,
          profileMatch.canonicalModelId,
          resolverOpts?.routingEnv,
        );
        const pricing = aiProfilesPricingToCatalogPricing(
          profileMatch.pricing,
          new Date().toISOString(),
          {
            promptTokens: input.tokens.prompt,
            routedViaOpenRouter,
          },
        );
        const { cost, breakdown } = computeCostFromPricing(
          input,
          pricing,
          routedViaOpenRouter,
          this.includeBreakdown,
        );

        warnings.push({
          code: "AI_PROFILES_ESTIMATE",
          message: `Catalog has no pricing for "${modelInput}"; estimated from ai-profiles (${profileMatch.matchedVia} → ${profileMatch.canonicalModelId}).`,
        });
        if (
          routedViaOpenRouter &&
          pricing.openRouterMarkupUsdPerInputToken !== undefined
        ) {
          warnings.push({
            code: "OPENROUTER_MARKUP_ESTIMATED",
            message:
              "OpenRouter markup estimated at 5% of base token rates (catalog markup fields absent).",
          });
        }

        console.warn(`[ai-tools] ${warnings.map((w) => w.message).join(" ")}`);

        return this.finish(
          {
            cost,
            breakdown,
            resolvedModelId: profileMatch.canonicalModelId,
            routedViaOpenRouter,
            isAuthoritative: false,
            pricingSnapshot: pricing,
            source: "ai-profiles",
            warnings,
          },
          input,
          resolved,
        );
      }
    }

    if (!resolved?.found || !resolved.record) {
      return this.unknownModelResult(modelInput, input, resolved);
    }

    const { record, modelId } = resolved;
    const routedViaOpenRouter = resolveRoutedViaOpenRouter(
      input.provider,
      resolved,
      modelId,
      resolverOpts?.routingEnv,
    );
    const pricing = record.pricing;
    const { cost, breakdown } = computeCostFromPricing(
      input,
      pricing,
      routedViaOpenRouter,
      this.includeBreakdown,
    );

    return this.finish(
      {
        cost,
        breakdown,
        resolvedModelId: modelId,
        routedViaOpenRouter,
        isAuthoritative: true,
        pricingSnapshot: pricing,
        source: "catalog",
      },
      input,
      resolved,
    );
  }

  private versionSuffixWarnings(
    input: AiUsageInput,
    pricedModelId: string,
    resolved?: ModelResolutionResult | null,
  ): AiCostWarning[] {
    const received = input.usedModel ?? input.modelUsed ?? input.model;
    if (!received) return [];

    const normalizedReceived = normalizeString(received);
    const normalizedPriced = normalizeString(pricedModelId);
    if (normalizedReceived === normalizedPriced) return [];

    const stripped = stripModelVersionSuffix(received);
    const viaResolver =
      resolved?.found === true &&
      resolved.resolvedVia.some(
        (s) => s === "version-suffix-strip" || s === "date-suffix-strip",
      );
    const pricedViaStrip =
      stripped !== null && normalizeString(stripped) === normalizedPriced;

    if (!viaResolver && !pricedViaStrip) return [];

    return [
      {
        code: "VERSION_SUFFIX_PRICING",
        message: `Priced using catalog model "${pricedModelId}" (no exact pricing for "${received}").`,
      },
    ];
  }

  private finish(
    partial: Omit<
      AiCostResult,
      "usage" | "provider" | "usedModel" | "model" | "extraction"
    >,
    input: AiUsageInput,
    resolved?: ModelResolutionResult | null,
  ): AiCostResult {
    const enriched = enrichCostResult(partial, input);
    const suffixWarnings = this.versionSuffixWarnings(
      input,
      partial.resolvedModelId,
      resolved,
    );
    if (suffixWarnings.length > 0) {
      enriched.warnings = [...(enriched.warnings ?? []), ...suffixWarnings];
    }
    return enriched;
  }

  private localProviderResult(
    modelInput: string,
    input: AiUsageInput,
    resolved: Awaited<ReturnType<typeof resolveFromCatalogAttempts>>,
  ): AiCostResult {
    const warnings: AiCostWarning[] = [
      {
        code: "LOCAL_PROVIDER_NO_PRICING",
        message: `Local provider "${input.provider}" has no catalog pricing for "${modelInput}".`,
      },
    ];
    console.warn(`[ai-tools] ${warnings[0]!.message}`);
    return this.finish(
      {
        cost: 0,
        resolvedModelId: resolved?.found ? resolved.modelId : modelInput,
        routedViaOpenRouter: false,
        isAuthoritative: false,
        pricingSnapshot: emptyPricing(),
        source: "local",
        warnings,
      },
      input,
    );
  }

  private unknownModelResult(
    modelInput: string,
    input: AiUsageInput,
    resolved: Awaited<ReturnType<typeof resolveFromCatalogAttempts>>,
  ): AiCostResult {
    if (this.throwOnUnknownModel) {
      throw new UnknownModelCostError(modelInput, input.provider);
    }

    const warnings: AiCostWarning[] = [
      {
        code: "UNKNOWN_MODEL",
        message: `Unknown model "${modelInput}" (provider: "${input.provider}") — returning zero-cost fallback.`,
      },
    ];
    console.warn(`[ai-tools] ${warnings[0]!.message}`);

    return this.finish(
      {
        cost: 0,
        resolvedModelId: modelInput,
        routedViaOpenRouter: resolveRoutedViaOpenRouter(
          input.provider,
          resolved,
          modelInput,
          this.resolverOptions?.routingEnv,
        ),
        isAuthoritative: false,
        pricingSnapshot: emptyPricing(),
        source: "estimate-fallback",
        unknownModel: true,
        warnings,
      },
      input,
    );
  }
}
